'use client';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import React, { CSSProperties, memo, useId } from 'react';

// Configuration object for all size-related variables
const CONFIG = {
  // Main background container
  background: {
    height: '50vh',
  },
  // Grid structure
  grid: {
    columns: 6,
    rows: 10,
    blockWidth: 120, // Smaller grid blocks (reduced from 240px)
    topPadding: 40, // Reduced top padding (from 64px)
    endWithDots: true, // Flag to ensure grid ends with dots rather than lines
    alignmentPadding: 2, // Extra padding to ensure perfect alignment
  },
  // Dot component
  dot: {
    outerSize: 16, // Smaller dots (reduced from 24px)
    innerSize: 5, // Smaller inner dots (reduced from 8px)
    pulseAnimation: {
      scale: [1, 1.2, 1],
      duration: 8, // seconds
      delay: (index: number) => (index * 0.2) % 4, // staggered animation
    },
  },
  // Horizontal line SVG
  horizontalLine: {
    width: 70, // Shorter lines (reduced from 140px)
    height: 1,
    strokeWidth: 1,
    animationDuration: 4, // Faster animation (reduced from 5s)
    animationDelay: 2, // Less delay (reduced from 5s)
    repeatDelay: 0.5, // Less repeat delay (reduced from 2s)
    pathLength: 240, // Updated path length
    opacity: 0.7, // Added transparency
  },
  // Vertical line SVG
  verticalLine: {
    width: 1,
    height: 70, // Shorter lines (reduced from 140px)
    strokeWidth: 1,
    animationDuration: 4, // Faster animation
    animationDelay: 3, // Staggered from horizontal
    repeatDelay: 0.5, // Less repeat delay
    pathLength: 240, // Updated path length
    marginLeft: 8, // Reduced margin (from 12px)
    opacity: 0.7, // Added transparency
  },
  // Particles
  particles: {
    count: 15,
    size: 2,
    opacity: 0.3,
    speed: 0.5,
  },
  // Colors
  colors: {
    light: {
      background: 'white',
      dot: {
        outer: 'white',
        inner: 'rgb(229, 229, 229)', // neutral-200
        glow: 'rgba(245, 245, 245, 0.5)', // subtle glow
      },
      line: 'rgb(245, 245, 245)', // neutral-100
      particle: 'rgb(229, 229, 229)', // neutral-200
    },
    dark: {
      background: 'black',
      dot: {
        outer: 'rgb(23, 23, 23)', // neutral-900
        inner: 'rgb(64, 64, 64)', // neutral-700
        glow: 'rgba(38, 38, 38, 0.5)', // subtle glow
      },
      line: 'rgb(38, 38, 38)', // neutral-800
      particle: 'rgb(64, 64, 64)', // neutral-700
    },
  },
};

// Use memo to prevent unnecessary re-renders
export const Background = memo(() => {
  return (
    <div
      className="fixed top-[5rem] left-[2.8%] right-0 w-full pointer-events-none z-[-1] flex justify-center items-start overflow-hidden"
      style={{ height: CONFIG.background.height }}
    >
      {/* Background gradient with enhanced mask */}
      <div className="absolute inset-0 h-full w-full bg-white dark:bg-black pointer-events-none [mask-image:radial-gradient(ellipse_at_center,transparent_30%,white_70%)]" />

      {/* Floating particles */}
      <FloatingParticles />

      {/* Grid structure with precise alignment */}
      <div
        className="flex flex-col items-center justify-start"
        style={{
          paddingTop: CONFIG.grid.topPadding,
          margin: 0,
          position: 'relative',
        }}
      >
        {Array.from({ length: CONFIG.grid.columns }).map((_, colIndex) => (
          <div className="flex" key={'grid-column' + colIndex} style={{ margin: 0, padding: 0 }}>
            {Array.from({ length: CONFIG.grid.rows }).map((_, rowIndex) => {
              // Check if this is the last element in the row
              const isLastInRow = rowIndex === CONFIG.grid.rows - 1;

              // Use appropriate block type based on position
              return CONFIG.grid.endWithDots && isLastInRow ? (
                <EdgeGridBlock
                  key={`grid-row-${colIndex}-${rowIndex}`}
                  index={colIndex * CONFIG.grid.rows + rowIndex}
                />
              ) : (
                <GridBlock
                  key={`grid-row-${colIndex}-${rowIndex}`}
                  index={colIndex * CONFIG.grid.rows + rowIndex}
                />
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
});

// Standard grid block with horizontal and vertical lines
const GridBlock = ({ index = 0 }: { index?: number }) => {
  // Calculate staggered animation delay based on index
  const delay = (index * 0.05) % 2;

  return (
    <div
      className="flex flex-col items-start justify-center"
      style={{
        width: CONFIG.grid.blockWidth,
        padding: 0,
        margin: 0,
        position: 'relative',
      }}
    >
      <div className="flex items-center justify-center" style={{ margin: 0, padding: 0 }}>
        <Dot index={index} />
        <SVG delay={delay} />
      </div>
      <SVGVertical delay={delay + 0.3} />
    </div>
  );
};

// Edge grid block that only has dots (for row endings)
// Made to exactly match the structure and dimensions of regular grid blocks
const EdgeGridBlock = ({ index = 0 }: { index?: number }) => {
  return (
    <div
      className="flex flex-col items-start justify-center"
      style={{
        width: CONFIG.grid.blockWidth,
        padding: 0,
        margin: 0,
        position: 'relative',
      }}
    >
      <div className="flex items-center justify-center" style={{ margin: 0, padding: 0 }}>
        {/* Using standard dot with edge styling */}
        <Dot index={index} isEdge={true} />
        {/* Transparent spacer with exact same dimensions as SVG */}
        <div
          style={{
            width: CONFIG.horizontalLine.width,
            height: CONFIG.horizontalLine.height,
            visibility: 'hidden',
          }}
        />
      </div>
      {/* Transparent spacer with exact same dimensions as vertical SVG */}
      <div
        style={{
          height: CONFIG.verticalLine.height,
          width: CONFIG.verticalLine.width,
          marginLeft: CONFIG.verticalLine.marginLeft,
          visibility: 'hidden',
        }}
      />
    </div>
  );
};

const Dot = ({ index = 0, isEdge = false }: { index?: number; isEdge?: boolean }) => {
  const { outerSize, innerSize, pulseAnimation } = CONFIG.dot;

  // Calculate delay based on dot position
  const animationDelay =
    typeof pulseAnimation.delay === 'function' ? pulseAnimation.delay(index) : 0;

  // Keep consistent size for all dots to maintain alignment
  // Instead, we'll use a subtle glow effect to emphasize edge dots
  const outerStyle: CSSProperties = {
    width: outerSize,
    height: outerSize,
    borderRadius: '9999px', // rounded-full
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: isEdge ? '0 0 8px rgba(245, 245, 245, 0.4)' : '0 0 5px rgba(245, 245, 245, 0.3)',
  };

  const innerStyle: CSSProperties = {
    width: innerSize,
    height: innerSize,
    borderRadius: '9999px', // rounded-full
  };

  // Edge dots have slightly more prominent animation
  // But not so dramatic as to cause alignment issues
  const scaleAnimation = isEdge ? [1, 1.15, 1] : pulseAnimation.scale;

  return (
    <motion.div
      className="bg-white dark:bg-neutral-900"
      style={outerStyle}
      animate={{ scale: scaleAnimation }}
      transition={{
        duration: pulseAnimation.duration,
        repeat: Infinity,
        delay: animationDelay,
        ease: 'easeInOut',
      }}
      custom={{ '--dot-glow': 'rgba(245, 245, 245, 0.3)' }}
    >
      <motion.div className="bg-neutral-200 dark:bg-neutral-700" style={innerStyle} />
    </motion.div>
  );
};

const SVGVertical = memo(
  ({ className, delay = 0 }: { className?: string; delay?: number } = {}) => {
    const {
      width,
      height,
      strokeWidth,
      animationDuration,
      animationDelay,
      repeatDelay,
      pathLength,
      marginLeft,
      opacity,
    } = CONFIG.verticalLine;

    const id = useId();

    return (
      <motion.svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={cn('text-neutral-100 dark:text-neutral-800', className)}
        style={{ marginLeft, opacity }}
        initial={{ opacity: 0 }}
        animate={{ opacity }}
        transition={{ duration: 1 }}
      >
        <path d={`M0.5 0.5V${pathLength}`} stroke="currentColor" strokeWidth={strokeWidth} />
        <motion.path
          d={`M0.5 0.5V${pathLength}`}
          stroke={`url(#gradient-${id})`}
          strokeWidth={strokeWidth}
        />

        <defs>
          <motion.linearGradient
            id={`gradient-${id}`}
            initial={{ x1: 2, y1: -50, x2: 2, y2: -20 }}
            animate={{ x1: 2, y1: 150, x2: 2, y2: 200 }}
            transition={{
              repeat: Infinity,
              duration: animationDuration,
              delay: animationDelay + delay,
              repeatDelay: repeatDelay,
              ease: 'easeInOut',
            }}
            gradientUnits="userSpaceOnUse"
          >
            <motion.stop offset="0%" stopColor="transparent" />
            <motion.stop offset="50%" stopColor="var(--neutral-200)" />
            <motion.stop offset="100%" stopColor="transparent" />
          </motion.linearGradient>
        </defs>
      </motion.svg>
    );
  }
);

// Floating particles component
const FloatingParticles = () => {
  const { count, size, opacity, speed } = CONFIG.particles;

  // Generate random positions for particles
  const particles = Array.from({ length: count }).map((_, i) => ({
    id: i,
    x: Math.random() * 100, // random percentage across the width
    y: Math.random() * 100, // random percentage across the height
    size: Math.random() * size + 1, // random size between 1 and size+1
    duration: (Math.random() * 2 + 1) * speed * 10, // random duration
  }));

  return (
    <div className="absolute inset-0 overflow-hidden">
      {particles.map(particle => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full bg-neutral-200 dark:bg-neutral-700"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            opacity,
          }}
          animate={{
            y: ['0%', `${Math.random() * 20 - 10}%`, '0%'],
            x: ['0%', `${Math.random() * 20 - 10}%`, '0%'],
            opacity: [opacity, opacity * 1.5, opacity],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
};

const SVG = memo(({ className, delay = 0 }: { className?: string; delay?: number } = {}) => {
  const {
    width,
    height,
    strokeWidth,
    animationDuration,
    animationDelay,
    repeatDelay,
    pathLength,
    opacity,
  } = CONFIG.horizontalLine;

  const id = useId();

  return (
    <motion.svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('text-neutral-100 dark:text-neutral-800', className)}
      style={{ opacity }}
      initial={{ opacity: 0 }}
      animate={{ opacity }}
      transition={{ duration: 1 }}
    >
      <path d={`M0.5 0.5H${pathLength}`} stroke="currentColor" strokeWidth={strokeWidth} />
      <motion.path
        d={`M0.5 0.5H${pathLength}`}
        stroke={`url(#gradient-${id})`}
        strokeWidth={strokeWidth}
      />

      <defs>
        <motion.linearGradient
          id={`gradient-${id}`}
          initial={{ x1: -50, y1: 0, x2: -20, y2: 0 }}
          animate={{ x1: 150, y1: 0, x2: 200, y2: 0 }}
          transition={{
            repeat: Infinity,
            duration: animationDuration,
            delay: animationDelay + delay,
            repeatDelay: repeatDelay,
            ease: 'easeInOut',
          }}
          gradientUnits="userSpaceOnUse"
        >
          <motion.stop offset="0%" stopColor="transparent" />
          <motion.stop offset="50%" stopColor="var(--neutral-200)" />
          <motion.stop offset="100%" stopColor="transparent" />
        </motion.linearGradient>
      </defs>
    </motion.svg>
  );
});
